/**
 * Live (over-the-air) web-bundle updates for the Capacitor native app.
 *
 * The store-installed app ships a frozen copy of the web build. A newer bundle
 * reaches it only as a SIGNED, human-approved release: a normal deploy
 * publishes no OTA metadata. .github/workflows/ota-release.yml (behind the
 * `ota-release` GitHub Environment) signs the deployed bundle with an Ed25519
 * key held off the host and publishes /ota/version.json + /ota/manifest.json.
 * Runbook: docs/runbooks/ota-release.md.
 *
 *   1. On launch/resume, poll the tiny signed /ota/version.json.
 *   2. Verify it against the public key compiled into THIS bundle
 *      (VITE_OTA_PUBLIC_KEY; ota-signature.ts). No key, no Ed25519 in the
 *      WebView, an unsigned file or a bad signature: download nothing.
 *   3. Anti-rollback: refuse a release_counter <= the installed counter, or
 *      below the highest min_counter any verified release has declared. The
 *      installed counter is the higher of what this device has persisted and
 *      the floor compiled into this bundle (VITE_OTA_COUNTER_FLOOR: the release
 *      this build supersedes), so a fresh store install or a device with
 *      cleared storage never "updates" to an older release. A server rollback
 *      ships as a NEW, higher counter that carries the older bundle.
 *   4. If its native fingerprint matches the installed shell, fetch the signed
 *      manifest, require it to describe the same release, and download only
 *      changed files (Capgo delta download; every file is hash-verified
 *      against the signed list).
 *   5. Apply it at the NEXT COLD START, before the user is working -- never
 *      mid-session. (The plugin's own next() applies on background, which on
 *      Android includes launching the camera: a mid-form WebView reload.)
 *      A downloaded bundle that a later verified release revoked (min_counter)
 *      or that this build supersedes is deleted, never applied.
 *
 * Counters live in native Preferences (SharedPreferences, which survives a
 * WebView localStorage.clear()) AND localStorage; a read takes the higher.
 *
 * If the shell's fingerprint differs, the new JS needs native plugins this
 * install lacks: skip, and the store build (mobile-android.yml) carries it.
 * A bundle that fails to call notifyAppReady() in time is rolled back by the
 * plugin and auto-deleted.
 */
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Preferences } from '@capacitor/preferences';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import {
  parseOtaPayload,
  parseOtaPublicKeys,
  verifyOtaEnvelope,
  type OtaReleaseHeader,
  type OtaReleaseManifest,
} from './ota-signature';

const OTA_BASE_URL = (import.meta.env.VITE_OTA_BASE_URL || 'https://app.cmd-bridge.com').replace(/\/+$/, '');
const VERSION_URL = `${OTA_BASE_URL}/ota/version.json`;
const MANIFEST_URL = `${OTA_BASE_URL}/ota/manifest.json`;
/** Every live-update key shares this prefix; ErrorBoundary's "clear" keeps them. */
export const OTA_STATE_KEY_PREFIX = 'cmdbridge.ota.';
const READY_BUNDLE_KEY = `${OTA_STATE_KEY_PREFIX}readyBundle`;
const FAILED_VERSION_KEY = `${OTA_STATE_KEY_PREFIX}failedVersion`;
/** Highest release_counter this device has downloaded or runs. */
const INSTALLED_COUNTER_KEY = `${OTA_STATE_KEY_PREFIX}installedCounter`;
/** Highest min_counter declared by any release this device has verified. */
const MIN_COUNTER_KEY = `${OTA_STATE_KEY_PREFIX}minCounter`;
const RESUME_CHECK_INTERVAL_MS = 30 * 60 * 1000;

interface ReadyBundle {
  id: string;
  version: string;
  /** Absent on bundles downloaded before releases were signed: never applied. */
  release_counter?: number;
}

/** Per-file URL. The hash query keeps Cloudflare from serving a stale copy of an unhashed file name. */
export function fileDownloadUrl(fileName: string, fileHash: string): string {
  const path = fileName.split('/').map(encodeURIComponent).join('/');
  return `${OTA_BASE_URL}/${path}?h=${fileHash.slice(0, 16)}`;
}

async function getJson(url: string): Promise<unknown> {
  const res = await CapacitorHttp.get({ url, headers: { 'Cache-Control': 'no-cache' } });
  if (res.status !== 200) throw new Error(`GET ${url} -> ${res.status}`);
  return typeof res.data === 'string' ? (JSON.parse(res.data) as unknown) : (res.data as unknown);
}

function readStored<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/** Returns false when storage is unavailable. */
function writeStored(key: string, value: unknown): boolean {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

/** A whole number >= 0, or null. Never coerces "", "1.5", "1e3", " 7". */
function parseWholeNumber(raw: string | null | undefined): number | null {
  if (typeof raw !== 'string' || !/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  return Number.isSafeInteger(value) ? value : null;
}

/**
 * The floor compiled into this bundle (VITE_OTA_COUNTER_FLOOR): unset = 0,
 * malformed = null, which disables live updates (fail closed).
 */
function parseCompiledFloor(raw: string | undefined): number | null {
  if (raw == null || raw.trim() === '') return 0;
  return parseWholeNumber(raw.trim());
}

async function readPreferenceCounter(key: string): Promise<number> {
  try {
    const { value } = await Preferences.get({ key });
    return parseWholeNumber(value) ?? 0;
  } catch {
    return 0;
  }
}

function readLocalCounter(key: string): number {
  try {
    return parseWholeNumber(localStorage.getItem(key)) ?? 0;
  } catch {
    return 0;
  }
}

/** The higher of the native Preferences and localStorage copies; 0 if neither is readable. */
async function readCounter(key: string): Promise<number> {
  return Math.max(await readPreferenceCounter(key), readLocalCounter(key));
}

/**
 * Raises a persisted counter in both stores (never lowers it). Returns false
 * only if neither store accepted it: then there is no anti-rollback.
 */
async function raiseCounter(key: string, value: number): Promise<boolean> {
  const next = String(Math.max(await readCounter(key), value));
  let local = false;
  try {
    localStorage.setItem(key, next);
    local = true;
  } catch {
    // quota / storage disabled; the native copy may still hold it
  }
  const native = await Preferences.set({ key, value: next }).then(
    () => true,
    () => false,
  );
  return local || native;
}

const readReadyBundle = () => readStored<ReadyBundle>(READY_BUNDLE_KEY);
const writeReadyBundle = (bundle: ReadyBundle | null) => writeStored(READY_BUNDLE_KEY, bundle);

async function discardReadyBundle(ready: ReadyBundle): Promise<void> {
  writeReadyBundle(null);
  await CapacitorUpdater.delete({ id: ready.id }).catch(() => undefined);
}

function sameRelease(a: OtaReleaseHeader, b: OtaReleaseHeader): boolean {
  return (
    a.version === b.version &&
    a.release_counter === b.release_counter &&
    a.min_counter === b.min_counter &&
    a.nativeFingerprint === b.nativeFingerprint
  );
}

class LiveUpdateService {
  private checking: Promise<void> | null = null;
  private lastCheckAt = 0;
  private readonly publicKeys: Uint8Array<ArrayBuffer>[];
  /** Release this bundle supersedes (VITE_OTA_COUNTER_FLOOR); null = malformed. */
  private readonly counterFloor: number | null;

  constructor(
    private readonly buildSha: string | undefined = import.meta.env.VITE_BUILD_SHA,
    publicKey: string | undefined = import.meta.env.VITE_OTA_PUBLIC_KEY,
    counterFloor: string | undefined = import.meta.env.VITE_OTA_COUNTER_FLOOR,
  ) {
    this.publicKeys = parseOtaPublicKeys(publicKey);
    this.counterFloor = parseCompiledFloor(counterFloor);
  }

  private get enabled(): boolean {
    return Capacitor.isNativePlatform() && !!this.buildSha;
  }

  /**
   * Call once, as early as possible on boot. notifyAppReady() must run on
   * every launch or the plugin rolls this bundle back after appReadyTimeout.
   */
  async onLaunch(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    await CapacitorUpdater.notifyAppReady();
    if (!this.enabled) return;
    try {
      // A bundle that failed to boot was rolled back by the plugin. Remember
      // its version so we don't download it again and fail every launch; the
      // next release (new version, higher counter) is tried normally.
      const failed = await CapacitorUpdater.getFailedUpdate();
      if (failed?.bundle?.version) writeStored(FAILED_VERSION_KEY, failed.bundle.version);

      if (await this.applyReadyBundle()) return; // WebView is reloading
    } catch (err) {
      console.warn('[LiveUpdate] apply at launch failed', err);
    }
    void this.checkForUpdate();
  }

  /** Call when the app returns to the foreground; throttled. */
  onResume(): void {
    if (!this.enabled || Date.now() - this.lastCheckAt < RESUME_CHECK_INTERVAL_MS) return;
    void this.checkForUpdate();
  }

  /** The higher of the persisted installed counter and the floor compiled into this build. */
  private async installedCounter(): Promise<number> {
    return Math.max(await readCounter(INSTALLED_COUNTER_KEY), this.counterFloor ?? 0);
  }

  /**
   * True when a downloaded, never-applied release must not run any more: a
   * later verified release raised min_counter above it (revocation), or this
   * build itself already supersedes it (a store build with a higher floor).
   */
  private async isSuperseded(releaseCounter: number): Promise<boolean> {
    if (this.counterFloor !== null && releaseCounter <= this.counterFloor) return true;
    return releaseCounter < (await readCounter(MIN_COUNTER_KEY));
  }

  /**
   * Swap in a bundle downloaded during a previous session. Returns true when
   * a swap was started (the plugin reloads the WebView).
   */
  async applyReadyBundle(): Promise<boolean> {
    const ready = readReadyBundle();
    if (!ready) return false;

    // Downloaded before releases were signed (no verified counter): never run it.
    if (typeof ready.release_counter !== 'number' || !Number.isSafeInteger(ready.release_counter)) {
      await discardReadyBundle(ready);
      return false;
    }
    // A malformed compiled floor disables live updates entirely.
    if (this.counterFloor === null) return false;

    const { bundle: current } = await CapacitorUpdater.current();
    if (current.id === ready.id || ready.version === this.buildSha) {
      writeReadyBundle(null); // already running it
      return false;
    }

    // Revoked while it waited (a later release's min_counter is above it, even
    // if that release could not be installed here) or older than this build.
    if (await this.isSuperseded(ready.release_counter)) {
      console.warn('[LiveUpdate] discarding superseded download, release', ready.release_counter);
      await discardReadyBundle(ready);
      return false;
    }

    // Gone (store update reset, failed + auto-deleted) or errored: forget it.
    const { bundles } = await CapacitorUpdater.list();
    const stored = bundles.find((b) => b.id === ready.id);
    if (!stored || stored.status === 'error') {
      writeReadyBundle(null);
      return false;
    }

    // Launched from a deep link (magic-link sign-in, push tap): a reload now
    // would drop the navigation. Apply on a plain launch instead.
    const launch = await CapacitorApp.getLaunchUrl();
    if (launch?.url) return false;

    await CapacitorUpdater.set({ id: ready.id });
    return true;
  }

  checkForUpdate(): Promise<void> {
    if (!this.enabled) return Promise.resolve();
    if (this.publicKeys.length === 0) {
      // Fail closed: without a pinned key nothing fetched can be trusted.
      console.warn('[LiveUpdate] no OTA public key in this build; live updates disabled');
      return Promise.resolve();
    }
    if (this.counterFloor === null) {
      console.warn('[LiveUpdate] malformed VITE_OTA_COUNTER_FLOOR in this build; live updates disabled');
      return Promise.resolve();
    }
    if (!this.checking) {
      this.lastCheckAt = Date.now();
      this.checking = this.runCheck()
        .catch((err) => console.warn('[LiveUpdate] update check failed', err))
        .finally(() => {
          this.checking = null;
        });
    }
    return this.checking;
  }

  private async fetchVerified(url: string, kind: 'version'): Promise<OtaReleaseHeader>;
  private async fetchVerified(url: string, kind: 'manifest'): Promise<OtaReleaseManifest>;
  private async fetchVerified(url: string, kind: 'version' | 'manifest'): Promise<OtaReleaseHeader> {
    const payload = await verifyOtaEnvelope(await getJson(url), this.publicKeys);
    return kind === 'version' ? parseOtaPayload(payload, 'version') : parseOtaPayload(payload, 'manifest');
  }

  /** Why a verified release must not be installed, or null if it may be. */
  private async rejectReason(release: OtaReleaseHeader): Promise<string | null> {
    const installed = await this.installedCounter();
    if (release.release_counter <= installed) {
      return `release ${release.release_counter} is not newer than release ${installed} (installed, or superseded by this build)`;
    }
    const floor = await readCounter(MIN_COUNTER_KEY);
    if (release.release_counter < floor) {
      return `release ${release.release_counter} is below the minimum allowed release ${floor}`;
    }
    return null;
  }

  private async runCheck(): Promise<void> {
    const latest = await this.fetchVerified(VERSION_URL, 'version');

    // A verified release may only ever raise the floor. Persisting it is also
    // the storage probe: without durable counters there is no anti-rollback,
    // so nothing is installed.
    if (!(await raiseCounter(MIN_COUNTER_KEY, latest.min_counter))) {
      console.warn('[LiveUpdate] cannot persist release counters; skipping update');
      return;
    }

    // A pending download this release revokes must never be applied, even if
    // this release itself cannot be installed (other shell, failed download).
    const pending = readReadyBundle();
    if (
      pending &&
      typeof pending.release_counter === 'number' &&
      (await this.isSuperseded(pending.release_counter))
    ) {
      console.warn('[LiveUpdate] discarding revoked download, release', pending.release_counter);
      await discardReadyBundle(pending);
    }

    if (latest.version === this.buildSha) {
      // Already running this release (e.g. a store build of the same commit).
      await raiseCounter(INSTALLED_COUNTER_KEY, latest.release_counter);
      return;
    }

    const rejected = await this.rejectReason(latest);
    if (rejected) {
      console.warn('[LiveUpdate] refusing release:', rejected);
      return;
    }
    if (readStored<string>(FAILED_VERSION_KEY) === latest.version) return;
    if (readReadyBundle()?.release_counter === latest.release_counter) return; // already downloaded

    const { version: shellFingerprint } = await CapacitorUpdater.getBuiltinVersion();
    if (latest.nativeFingerprint !== shellFingerprint) {
      console.info('[LiveUpdate] newer web build needs a native store update; skipping', {
        latest: latest.nativeFingerprint,
        shell: shellFingerprint,
      });
      return;
    }

    const manifest = await this.fetchVerified(MANIFEST_URL, 'manifest');
    // A release can be published between the two fetches; trust only a consistent pair.
    if (!sameRelease(manifest, latest)) return;

    const bundle = await CapacitorUpdater.download({
      url: MANIFEST_URL,
      version: manifest.version,
      manifest: manifest.files.map((f) => ({
        file_name: f.file_name,
        file_hash: f.file_hash,
        download_url: fileDownloadUrl(f.file_name, f.file_hash),
      })),
    });

    // Recorded only after a complete, hash-verified download: a failed
    // download is retried, an installed release can never be installed again.
    if (!(await raiseCounter(INSTALLED_COUNTER_KEY, manifest.release_counter))) {
      await CapacitorUpdater.delete({ id: bundle.id }).catch(() => undefined);
      console.warn('[LiveUpdate] cannot persist the installed release counter; discarding download');
      return;
    }

    // Drop a previously downloaded, never-applied bundle superseded by this one.
    const previous = readReadyBundle();
    if (previous && previous.id !== bundle.id) {
      await CapacitorUpdater.delete({ id: previous.id }).catch(() => undefined);
    }
    writeReadyBundle({ id: bundle.id, version: bundle.version, release_counter: manifest.release_counter });
    console.info(
      '[LiveUpdate] downloaded release',
      manifest.release_counter,
      `(${bundle.version})`,
      '- applies on next launch',
    );
  }
}

export { LiveUpdateService };
export const liveUpdateService = new LiveUpdateService();
